import { MagnifyingGlassIcon, PenIcon, XIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { Suspense, lazy, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { Alert } from "@/components/Alert/Alert";
import { Button } from "@/components/Button";
import { DrawerContent, SwipeableDrawer } from "@/components/Drawer";
import { IconButton } from "@/components/IconButton/IconButton";
import { Radio } from "@/components/Radio/Radio";
import { RadioGroup } from "@/components/Radio/RadioGroup";
import { TextField } from "@/components/TextField/TextField";
import { showSnackbar } from "@/contexts/SnackbarContext";
import DeleteTraveller from "@/features/travellers/components/DeleteTraveller";
import { useDeleteTravellerMutation } from "@/features/travellers/hooks/useTravellers";
import type { Traveller } from "@/features/travellers/types/types";
import { getTravellerTypeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import {
  checkIfTravellerHasPassportDetails,
  getTravelerType,
} from "@/features/travellers/utils/transformTraveler";
import { rudderAnalytics, CommonTrackingEvents } from "@/lib/rudderstack";
import { numericDateFormatOptions } from "@/utils/dates";

import { TravelerTypeLabel } from "./TravelerTypeLabel";
import { TravellerActions } from "./TravellerActions";
const TravellerFormDrawer = lazy(() => import("./TravellerFormDrawer"));

type Props = {
  travellers: Traveller[];
  travelDate: Date;
  selectedTravellerId: string | null;
  travellerIdentifiableName?: string;
  hidePassportDetails?: boolean;
  onTravellerSelected: (travellerId: string) => void;
  onTravellerFormSubmitSuccess?: (
    traveller: Traveller,
    action: "create" | "update",
  ) => void;
  isSelectionDisabled?: (traveller: Traveller) => boolean;
};

export function TravellersList({
  travellers,
  travelDate,
  selectedTravellerId,
  travellerIdentifiableName,
  hidePassportDetails,
  onTravellerSelected,
  onTravellerFormSubmitSuccess,
  isSelectionDisabled,
}: Props) {
  const [travellerForDeletion, setTravellerForDeletion] =
    useState<Traveller | null>(null);
  const [travellerForEdit, setTravellerForEdit] = useState<Traveller | null>(
    null,
  );
  const [newTraveller, setNewTraveller] = useState(false);
  const intl = useIntl();

  const { mutateAsync: deleteTraveller } = useDeleteTravellerMutation();
  const [query, setQuery] = useState("");

  const initTravellerDeletion = (traveller: Traveller) => {
    setTravellerForDeletion(traveller);
  };

  const initTravellerEdit = (traveller: Traveller) => {
    setTravellerForEdit(traveller);
  };

  const closeDrawerHandler = () => {
    setTravellerForDeletion(null);
    setTravellerForEdit(null);
    setNewTraveller(false);
  };

  const filteredTravellers = useMemo(
    () =>
      travellers.filter((traveller) =>
        `${traveller.firstName.toLocaleLowerCase()} ${traveller.lastName.toLocaleLowerCase()}`.includes(
          query.toLocaleLowerCase(),
        ),
      ),
    [query, travellers],
  );

  const shouldValidatePassportDetails = !hidePassportDetails;

  return (
    <>
      <div className="section-card mb-4 text-center">
        <p className="mb-1 text-base font-medium text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Traveller not on the list?",
            description:
              "Heading shown when a searched traveller is not found in the saved list",
          })}
        </p>
        <p className="text-sm font-normal text-text-secondary">
          {intl.formatMessage({
            defaultMessage: "Add them manually in a few steps",
            description:
              "Explanatory text when a searched traveller is not found in the saved list",
          })}
        </p>
        <div className="mt-4 mb-2">
          <Button
            variant="outlined"
            size="small"
            color="primary"
            fullWidth
            onClick={() => {
              setNewTraveller(true);
              rudderAnalytics.track(
                CommonTrackingEvents.USER_ENTERED_CREATING_TRAVELER,
              );
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Add new traveller",
              description: "Button to create a new traveller profile",
            })}
          </Button>
        </div>
      </div>
      <div className="section-title">
        {intl.formatMessage({
          defaultMessage: "Saved Travellers",
          description:
            "Section heading above the list of previously saved traveller profiles",
        })}
      </div>
      <TextField
        placeholder={intl.formatMessage({
          defaultMessage: "Search by name",
          description:
            "Placeholder or label for the traveller search-by-name input field",
        })}
        fullWidth
        size="small"
        startAdornment={
          <MagnifyingGlassIcon
            size={20}
            color="var(--color-text-secondary)"
            weight="bold"
            className="me-2"
          />
        }
        endAdornment={
          query ? (
            <IconButton onClick={() => setQuery("")}>
              <XIcon size={20} color="var(--color-stone)" weight="bold" />
            </IconButton>
          ) : null
        }
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="mt-2">
        {filteredTravellers.length > 0 ? (
          <div className="rounded-2xl bg-white">
            <RadioGroup value={selectedTravellerId || ""}>
              {filteredTravellers?.map((traveller) => {
                const ageGroupAtTravelDate = getTravellerTypeAtTravelDate(
                  traveller.birthDate,
                  travelDate,
                );
                const ageGroup = getTravelerType(traveller.birthDate);
                const hasAgeGroupMismatch = ageGroup !== ageGroupAtTravelDate;

                const hasPassportDetails = shouldValidatePassportDetails
                  ? checkIfTravellerHasPassportDetails(traveller)
                  : true;

                // block the traveller selection if the first travel date (check-in/departure)
                // is less than 6 months from the passport expiry date
                const isPassportExpired =
                  shouldValidatePassportDetails &&
                  dayjs(travelDate)
                    .add(6, "months")
                    .isAfter(dayjs(traveller.passportExpiryDate));

                const isDisabled = isSelectionDisabled?.(traveller);

                let message: string | null = null;
                if (!isDisabled) {
                  if (!hasPassportDetails) {
                    message = intl.formatMessage({
                      defaultMessage: "Required data is missing",
                      description:
                        "Error shown when a traveller is missing required passport details for travel",
                    });
                  } else if (isPassportExpired) {
                    message = intl.formatMessage({
                      defaultMessage: "Passport expires in 6 months",
                      description:
                        "Hint text explaining passport expiry date requirements for travel",
                    });
                  }
                }
                const shouldPromptEdit = !!message;

                return (
                  <div
                    key={traveller.id}
                    className="flex flex-col gap-2 px-2 py-4"
                  >
                    <div className="flex justify-between gap-2">
                      <Radio
                        value={traveller.id}
                        className="m-0 flex gap-2 [label]:grow"
                        onChange={(e) => {
                          if (shouldPromptEdit) {
                            showSnackbar({
                              type: "success",
                              content: intl.formatMessage({
                                defaultMessage: "Complete missing information",
                                description:
                                  "Text shown in snackbar when trying to select a traveller with incomplete or invalid data, prompting the user to edit the profile and complete the missing/invalid data",
                              }),
                            });

                            return;
                          }

                          onTravellerSelected(traveller.id);

                          rudderAnalytics.track(
                            e.target.checked
                              ? CommonTrackingEvents.USER_SELECTED_TRAVELER
                              : CommonTrackingEvents.USER_DESELECTED_TRAVELER,
                            {
                              traveler_type: getTravelerType(
                                traveller.birthDate,
                              ),
                              traveler_type_at_travel_date:
                                getTravellerTypeAtTravelDate(
                                  traveller.birthDate,
                                  travelDate,
                                ),
                            },
                          );
                        }}
                        disabled={isDisabled}
                        label={
                          <div
                            className="flex flex-1 flex-col"
                            data-sentry-mask
                          >
                            <span
                              className={clsx(
                                "text-base font-medium",
                                isDisabled
                                  ? "text-text-disabled-primary"
                                  : "text-text-primary",
                              )}
                            >
                              {traveller.firstName} {traveller.lastName}
                            </span>

                            {!message && (
                              <span
                                className={clsx(
                                  "text-sm",
                                  isDisabled
                                    ? "text-text-disabled-secondary"
                                    : "text-text-secondary",
                                )}
                              >
                                <TravelerTypeLabel {...traveller} />{" "}
                                <span className="text-stroke-neutral-medium">
                                  &bull;
                                </span>{" "}
                                {intl.formatDate(
                                  traveller.birthDate,
                                  numericDateFormatOptions,
                                )}
                              </span>
                            )}

                            {!!message && (
                              <span
                                className={
                                  "mt-0.5 text-sm text-semantic-error-strong"
                                }
                              >
                                {message}
                              </span>
                            )}
                          </div>
                        }
                        labelPlacement="end"
                      />

                      <div className="flex shrink-0 items-center">
                        {shouldPromptEdit ? (
                          <IconButton
                            onClick={() => {
                              initTravellerEdit(traveller);
                            }}
                            color="primary"
                          >
                            <PenIcon size={24} />
                          </IconButton>
                        ) : (
                          <TravellerActions
                            traveller={traveller}
                            disabled={isDisabled}
                            onDelete={initTravellerDeletion}
                            onEdit={initTravellerEdit}
                          />
                        )}
                      </div>
                    </div>

                    {hasAgeGroupMismatch && (
                      <div className="px-2">
                        <Alert severity="info">
                          {intl.formatMessage({
                            defaultMessage:
                              "Child/adult classification depends on age at travel date.",
                            description:
                              "Hint text explaining why a traveller's age group classification may differ from what the user expects, based on the traveller's age at the travel date",
                          })}
                        </Alert>
                      </div>
                    )}
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 py-10">
            <div className="font-medium text-black">
              {intl.formatMessage({
                defaultMessage: "Nothing found",
                description:
                  "Message shown when a traveller name search returns no results",
              })}
            </div>
            <div className="text-center text-sm text-text-placeholder">
              {query
                ? intl.formatMessage({
                    defaultMessage: "Please check your input for typos",
                    description:
                      "Hint to check for typos when a traveller name search returns no results",
                  })
                : intl.formatMessage({
                    defaultMessage: "No saved travellers to choose from",
                    description:
                      "Message shown when the user has no saved traveller profiles",
                  })}
            </div>
          </div>
        )}
      </div>

      <Suspense fallback={null}>
        <TravellerFormDrawer
          travellerIdentifiableName={travellerIdentifiableName}
          open={Boolean(newTraveller || travellerForEdit)}
          hidePassportDetails={hidePassportDetails}
          onSubmitSuccess={(traveller, action) => {
            onTravellerFormSubmitSuccess?.(traveller, action);
            closeDrawerHandler();
          }}
          onClose={() => {
            closeDrawerHandler();
          }}
          traveller={travellerForEdit}
        />
      </Suspense>

      <SwipeableDrawer
        open={Boolean(travellerForDeletion)}
        onClose={() => setTravellerForDeletion(null)}
      >
        <DrawerContent>
          {travellerForDeletion ? (
            <DeleteTraveller
              traveller={travellerForDeletion}
              deleteTraveller={() => deleteTraveller(travellerForDeletion.id)}
              closeDrawer={closeDrawerHandler}
            />
          ) : null}
        </DrawerContent>
      </SwipeableDrawer>
    </>
  );
}
